import { BaseController } from '../../common/base/controller';
import { FileResponse } from '@fino/ng1-response/lib';

import _ from 'lodash';

export class AdminController extends BaseController {
  constructor(
    $scope,
    $filter,
    controllerService,
    adminService,
    hotkeys,
    clipboard,
    notificationService,
    adminUserTablePartial,
    roleDescriptions,
    userImportService,
    fileResponseService,
    inputFormService,
  ) {
    'ngInject';
    super({
      $scope,
      $filter,
      controllerService,
      adminService,
      hotkeys,
      clipboard,
      notificationService,
      adminUserTablePartial,
      roleDescriptions,
      userImportService,
      fileResponseService,
      inputFormService,
    });
  }

  usersPagination = {
    offset: 0,
    pageSize: 5,
  };

  $onInit() {
    const { adminService } = this;
    this.users = [];

    // Atruvia Tenants have YC Usernames
    const { transferringUpload } = this.config;
    if (transferringUpload.startsWith('AGREE')) {
      this.userNameTranslateKeyPostfix = 'YC';
    }

    this.loadUsers();

    // Fetch roles
    adminService
      .getRoles()
      .then(this.rolesFromServer.bind(this))
      .catch(this.handleError.bind(this));

    this.roles = [];

    this.messages = {
      required: 'admin.userTable.errors.required',
      email: 'admin.userTable.errors.email',
    };

    this.bindHotkeys();

    // search value removed, autoreload users
    this.$scope.$watch('vm.searchValue', (newValue, oldValue) => {
      if (oldValue && !newValue) {
        this.loadUsers();
      }
    });
  }

  searchByEmail() {
    this.usersPagination.offset = 0;
    this.loadUsers();
  }

  bindHotkeys() {
    const { hotkeys, $scope } = this;

    // Bind Hotkeys to the controllers scope
    hotkeys
      .bindTo($scope)
      .add({
        combo: 'enter',
        description: 'Save User',
        callback: () => this.saveCurrentUser(),
      })
      .add({
        combo: 'esc',
        description: 'Cancel Edit',
        callback: (event) => this.cancelEdit(event),
      });
  }

  loadUsers() {
    if (this.usersLoading) return;
    this.usersLoading = true;
    this._loadUsersCanceller = this.adminService.$q.defer();
    this.adminService
      .getUsers(
        this.searchValue,
        this.usersPagination.offset,
        this.usersPagination.pageSize,
        this._loadUsersCanceller.promise,
      )
      .then(this.usersFromServer.bind(this))
      .catch((response) => {
        // status -1 means the request was aborted intentionally — don't show an error
        if (response.status !== -1) {
          this.handleError(response);
        }
      })
      .finally(() => {
        this.usersLoading = false;
        this._loadUsersCanceller = null;
      });
  }

  cancelLoadUsers() {
    if (this._loadUsersCanceller) {
      this._loadUsersCanceller.resolve();
    }
  }

  usersFromServer(users) {
    // request was intentionally cancelled — keep the existing list as-is
    if (users && users._cancelled) return;
    // if admin is creating new users, dont let the edit field disappear
    this.users =
      this.currentUser && this.currentUser.isNew
        ? [this.currentUser, ...users.data]
        : users.data;
  }

  isCurrentUser(user) {
    if (!user || !this.currentUser) return false;
    return this.currentUser._id === user._id;
  }

  rolesFromServer(roles) {
    this.roles = roles.data;

    this.roleOptions = {
      modelKey: 'key',
      selectables: this.roles.map((role) => {
        return {
          key: role,
          value: `admin.roles.${role}.label`,
        };
      }),
    };
  }

  updatedUserFromServer(response) {
    _.assign(this.currentUser, response.data);
    delete this.currentUser;
    delete this.errorMessage;
  }

  deletedUserFromServer(response) {
    const deletedUserId = response.data;
    _.remove(this.users, { _id: deletedUserId });
    delete this.currentUser;
  }

  openImportUserModal() {
    this.userImportService.openModal().result.then((result) => {
      this.notificationService.success(
        this.$translate.instant('app.admin.userImport.success'),
      );
      this.adminService
        .getUsers()
        .then(this.usersFromServer.bind(this))
        .catch(this.handleError.bind(this));
    });
  }

  handleError(response) {
    const { status, data } = response;

    if (status === 400) {
      return this.notificationService.error(
        'reset.password.byMail.error.400.bad.request',
      );
    }
    if (status === 403) {
      return (this.errorMessage = 'app.error.insufficient.permission');
    }
    if (status === 409) {
      return this.notificationService.warning(data.error);
    }

    return this.notificationService.error('app.error.unknown');
  }

  maybeCancelEdit($event) {
    if ($event.keyCode === 27) {
      this.cancelEdit($event);
    }
  }

  isDeletable(user) {
    return !!user;
  }

  isDisable(user) {
    return user && user.role !== 'ADMIN';
  }

  createUser() {
    if (!this.currentUser) {
      this.originalUser = null;
      this.currentUser = {
        role: this.roles[0],
        status: 'ACTIVE',
        isNew: true,
      };
      this.users.unshift(this.currentUser);
      this.infoMessage = 'app.admin.newUser.info';
    }
  }

  editUser(user) {
    if (!this.currentUser) {
      this.originalUser = angular.copy(user);
      this.currentUser = user;
      this.currentUser.isNew = false;
      this.usersForm.$setSubmitted();
    }
  }

  maybeEditUser(user) {
    if (!this.currentUser) {
      this.editUser(user);
    }
  }

  cancelEdit($event) {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.currentUser) {
      if (this.currentUser.isNew) {
        this.users.shift();
      } else {
        _.assign(this.currentUser, this.originalUser);
      }
      this.currentUser = null;
      this.infoMessage = false;
    }
  }

  saveCurrentUser() {
    const { adminService } = this;
    if (this.currentUser.isNew) {
      if (
        this.isFeatureActive(this.FeatureNames.FF_DISABLE_CHECK_ASCII_LETTER) &&
        this.hasUserNameNotSupportedSigns()
      ) {
        return;
      }

      adminService
        .createUser(this.currentUser)
        .then(this.updatedUserFromServer.bind(this))
        .catch(this.handleError.bind(this))
        .finally(() => (this.infoMessage = false));
    } else {
      adminService
        .updateUser(this.currentUser)
        .then(this.updatedUserFromServer.bind(this))
        .catch(this.handleError.bind(this));
    }
  }

  hasUserNameNotSupportedSigns() {
    const { sub } = this.currentUser;
    // check that all characters are ASCII chars
    let result = sub.match('^[\x00-\x7F]*$');

    if (!result) {
      this.notificationService.error('login.error.asciiletters');
      return true;
    }

    return false;
  }

  deleteUser($event, user) {
    $event.preventDefault();
    $event.stopPropagation();
    if (
      this.$window.confirm(
        this.$filter('translate')('app.admin.delete.user.confirmation', {
          user: user,
        }),
      )
    ) {
      this.adminService
        .deleteUser(user)
        .then(this.deletedUserFromServer.bind(this))
        .catch(this.handleError.bind(this));
    }
  }

  resetPassword($event, user) {
    $event.preventDefault();
    $event.stopPropagation();
    if (
      this.$window.confirm(
        this.$filter('translate')('app.admin.reset.password.confirmation', {
          user: user,
        }),
      )
    ) {
      this.currentUser = user;
      this.adminService
        .resetPassword(user)
        .then(this.updatedUserFromServer.bind(this))
        .catch(this.handleError.bind(this));
    }
  }

  resetPasswordByEmail($event, user) {
    user.sendMailCommandRunning = true;
    $event.preventDefault();
    $event.stopPropagation();
    this.adminService
      .resetPasswordByEmail(user)
      .then(() => {
        this.notificationService.success(
          'admin.userTable.user.sendMailSuccess',
        );
      })
      .finally(() => {
        user.sendMailCommandRunning = false;
      })
      .catch(this.handleError.bind(this));
  }

  disableUser(user) {
    if (user) {
      user.status = 'INACTIVE';
    }
  }

  enableUser(user) {
    if (user) {
      user.status = 'ACTIVE';
    }
  }

  createPasswordResetUrl(user) {
    return this.$state.href(
      'app.resetPassword',
      { token: user.passwordResetToken, user: user.displayName },
      { location: true, absolute: true },
    );
  }

  copyResetUrlToClipboard($event, user) {
    $event.preventDefault();
    $event.stopPropagation();

    const validUntil = new Date(user.passwordResetExpirationDate);
    const expired = validUntil <= new Date();

    if (expired) {
      this.notificationService.warning('app.admin.password.reset.url.expired', {
        expires: validUntil,
      });
    } else {
      const resetUrl = this.createPasswordResetUrl(user);
      this.clipboard.copyText(resetUrl);
      this.notificationService.success(
        'app.admin.password.reset.url.clipboard.copied',
        { expires: validUntil.getTime() },
      );
    }
  }

  exportAllUsers() {
    if (this.exportAllUsersRunning) return;
    this.exportAllUsersRunning = true;
    const { adminService } = this;
    adminService
      .exportAllUsers()
      .then(FileResponse.transform)
      .then((file) => this.fileResponseService.saveAs(file, 'application/csv'))
      .finally(() => (this.exportAllUsersRunning = false));
  }

  get showAuditExport() {
    return this.isFeatureActive(
      this.FeatureNames.FF_SHOW_DOWNLOAD_USER_CHANGE_HISTORY,
    );
  }

  exportAudit() {
    if (this.exportAuditRunning) return;
    this.exportAuditRunning = true;
    const { adminService } = this;
    adminService
      .exportAudit()
      .then(FileResponse.transform)
      .then((file) => this.fileResponseService.saveAs(file, 'application/csv'))
      .finally(() => (this.exportAuditRunning = false));
  }

  goNext() {
    this.usersPagination.offset += this.usersPagination.pageSize;
    this.loadUsers();
  }

  get canGoNext() {
    return this.users && this.users.length >= this.usersPagination.pageSize;
  }

  get canGoPrevious() {
    return this.usersPagination.offset > 0;
  }

  goPrevious() {
    this.usersPagination.offset -= this.usersPagination.pageSize;
    if (this.usersPagination.offset < 0) {
      this.usersPagination.offset = 0;
    }
    this.loadUsers();
  }
}
